import React, {Fragment, useEffect, useState} from 'react';
import i18n from 'i18next';
import { useForm, useWatch  } from "react-hook-form"
import Button from '@/Components/Button';
import '../../../sass/authenticated/profile-settings-slider.scss';
import '@/../sass/authenticated/slider.scss';
import {FormInput} from "@/Components/Controls";
import MyGivingIcon from "../../../icons/my-giving-slider.svg";
import MyChurchProfileIcon from "../../../icons/my-church-profile-slider.svg";
import bffApiUrlService from "@/Utilities/BffApiUrlService";
import SliderToastSuccessMessage from "@/Components/SliderToastSuccessMessage";
import SliderToastFailedMessage from "@/Components/SliderToastFailedMessage";
import {ProfileSliderTypes} from "@/Enums/ProfileSliderTypes";
import ProfileSliderFormCard from "@/Components/ProfileSliderFormCard";

export default function MyProfileSlider({show, userUuid, organizationUuid, toggleMyProfile}) {
    const [sliderOpen, setSliderOpen] = useState(false);
    const [currentUserData, setCurrentUserData] = useState({
        first_name: '',
        last_name: '',
        contact_email_data: {'communication_uuid': null, 'method': 'email', 'title': 'primary', 'value': ''},
        contact_phone_data:  {'communication_uuid': null, 'method': 'phone', 'title': 'cell', 'value': ''}
    });
    const [formDataModified, setFormDataModified] = useState(false);
    const [disableForm, setDisableForm] = useState(false);
    const [myGivingUrl, setMyGivingUrl] = useState(null);
    const [myChurchProfileUrl, setMyChurchProfileUrl] = useState(null);
    const [successMessage, setSuccessMessage] = useState({
        isVisible: false,
        messageType: 'standard_success'
    });
    const [failedMessage, setFailedMessage] = useState({
        isVisible: false,
        messageType: 'standard_error'
    });

    const { control, formState: { errors }, reset,
        getValues, setValue} =
        useForm({
            defaultValues: {
                first_name: '',
                last_name: '',
                contact_email_data: {'communication_uuid': null, 'method': 'email', 'title': 'primary', 'value': ''},
                contact_phone_data: {'communication_uuid': null, 'method': 'phone', 'title': 'cell', 'value': ''}
            },
        });

    const watchedFormValues = useWatch({
        control,
        name: ["first_name", "last_name", "contact_email_data.value", "contact_phone_data.value"]
    });

    useEffect(() => {
        if(sliderOpen){
            setFormDataModified(isFormDataModified());
        }
    }, [watchedFormValues]);

    useEffect(() => {
        if (show) {
            fetchMyProfile();
        }
        else{
            setSuccessMessage({isVisible: false, messageType: 'standard_success'});
            setFailedMessage({isVisible: false, messageType: 'standard_error'});
        }
    }, [show]);

    const fetchMyProfile = () => {
        axios.get(bffApiUrlService.getMyProfileEndpoint(userUuid), {
            headers: {
                'OrganizationUuid': organizationUuid
            }
        }).then(
            response => {
                let userDataResponse = response.data;
                handleMyProfileResponseData(userDataResponse);
                setDisableForm(false);
                setSliderOpen(true);
            }
        ).catch(error => {
            setDisableForm(true);
            setSliderOpen(true);
            setFailedMessage({isVisible: true, messageType: 'loading_error'});
        });
    }

    const isFormDataModified = () => {
        let formData = getValues();
        return (formData.first_name !== currentUserData.first_name
        || formData.last_name !== currentUserData.last_name
        || formData.contact_email_data.value !== currentUserData.contact_email_data.value
        || formData.contact_phone_data.value !== currentUserData.contact_phone_data.value);
    }

    const handleMyProfileResponseData = (userDataResponse) => {
        const userData = {
            first_name: userDataResponse.first_name ?? '',
            last_name: userDataResponse.last_name ?? '',
            contact_email_data: userDataResponse.contact_email_data ?? currentUserData.contact_email_data,
            contact_phone_data: userDataResponse.contact_phone_number_data ?? currentUserData.contact_phone_data
        };

        reset(userData);
        setCurrentUserData(userData)
        setMyChurchProfileUrl(userDataResponse.my_church_profile_url);
        setMyGivingUrl(userDataResponse.my_giving_url);
    }

    const save = (e) => {
        e.preventDefault();

        axios.put(route('manage.myprofile.update'), getValues()).then(
            response => {
                setValue("first_name", getValues().first_name);
                setValue("last_name", getValues().last_name);
                setCurrentUserData({...currentUserData,
                    first_name: getValues().first_name,
                    last_name: getValues().last_name});
                setFormDataModified(false);
                setSuccessMessage({isVisible: true, messageType: 'standard_success'});
                setFailedMessage({isVisible: false, messageType: 'standard_error'});
            }
        ).catch(error => {
            setSuccessMessage({isVisible: false, messageType: 'standard_success'});
            setFailedMessage({isVisible: true, messageType: 'standard_error'});
        });
    };

    const onChange = (event, value) => {
        setValue(event, value);
    };

    const closeSlider = () => {
        setSliderOpen(false);
        toggleMyProfile();
    };

    const formData = getValues();

    return (
        <Fragment>
            {sliderOpen && <div className={"fade modal-backdrop slider-backdrop show"} />}
            <div className={sliderOpen ? "profile-settings-slider show" : "profile-settings-slider"} data-testid="profile-settings-slider-my_profile">
                <div className='slider-div'>
                    <div className="profile-div">
                        <div className="header-div">
                            <Button type="button" className="back-button buttons"
                                    onClick={() => closeSlider()}>
                                <div className="back-button-content">
                                    <div className="back-button-content-chevron-left-div">
                                        <i className="fas fa-chevron-left"></i>
                                    </div>
                                    <div className="back-button-text">
                                        {i18n.t("my-profile-slider.back")}
                                    </div>
                                </div>
                            </Button>
                            <div className="title">{i18n.t("my-profile-slider.title")}</div>
                            <div className="header-right-div" />
                        </div>
                        <img className="fill" src="/images/userprofile-slider-gradient.svg" alt="" />
                    </div>
                    <div className="profile-info-div">
                        <div className="profile-info-form-div">
                            <div className="profile-info-header">
                                {i18n.t("my-profile-slider.form-header")}
                            </div>
                            <form className="profile-form">
                                <FormInput error={errors["first_name"]} name="first_name" isFocused={true}
                                           autoComplete="first_name" value={formData.first_name} onChange={onChange}
                                           label={i18n.t("my-profile-slider.first-name")}
                                           className="form-input" onKeyUp={e => e.keyCode === 13}
                                           dataTestid="my_profile_slider-input-first_name" disabled={disableForm}/>
                                <FormInput error={errors["last_name"]} name="last_name"
                                           autoComplete="last_name" value={formData.last_name} onChange={onChange}
                                           label={i18n.t("my-profile-slider.last-name")}
                                           className="form-input" onKeyUp={e => e.keyCode === 13}
                                           dataTestid="my_profile_slider-input-last_name" disabled={disableForm}/>
                                <FormInput error={errors["contact_email_data.value"]} name="contact_email_data.value"
                                           isFocused={true} autoComplete="contact_email" value={formData.contact_email_data.value}
                                           onChange={onChange} label={i18n.t("my-profile-slider.contact-email")}
                                           className="form-input" onKeyUp={e => e.keyCode === 13}
                                           dataTestid="my_profile_slider-input-contact_email" disabled={disableForm}/>
                                <FormInput error={errors["contact_phone_data.value"]}
                                           name="contact_phone_data.value"
                                           isFocused={true} autoComplete="contact_phone" value={formData.contact_phone_data.value}
                                           onChange={onChange} label={i18n.t("my-profile-slider.contact-phonenumber")}
                                           className="form-input" onKeyUp={e => e.keyCode === 13}
                                           dataTestid="my_profile_slider-input-contact_phonenumber" disabled={disableForm}/>
                            </form>
                        </div>
                    </div>
                    <div className="form-cards-div">
                        {myChurchProfileUrl &&
                            <ProfileSliderFormCard linkUrl={myChurchProfileUrl} linkIcon={MyChurchProfileIcon}
                                                   linkTitle={i18n.t("my-profile-slider.my-church-profile")}/>
                        }
                        {myGivingUrl &&
                            <ProfileSliderFormCard linkUrl={myGivingUrl} linkIcon={MyGivingIcon}
                                                   linkTitle={i18n.t("my-profile-slider.my-giving")}/>
                        }
                    </div>
                    <div className="actions-footer">
                        <a href="#"
                            className="request-account-deletion-button buttons"
                            onClick={(e) => {
                               e.preventDefault();
                               axios.delete(route('manage.profile.destroy')).then(() => {
                                   location.reload();
                               });
                           }}
                        >
                            {i18n.t("my-profile-slider.request-account-deletion")}
                        </a>
                        <div className="actions-footer-right-buttons-div">
                            <Button type="button" className="cancel-button buttons"
                                    onClick={() => closeSlider()} value="Cancel">
                                {i18n.t("my-profile-slider.cancel")}
                            </Button>
                            <Button className={`save-button${!formDataModified || disableForm ? "-disabled" : ""} buttons`} onClick={save}>
                                {i18n.t("my-profile-slider.save")}
                            </Button>
                        </div>
                    </div>
                </div>
                {successMessage.isVisible &&
                    <SliderToastSuccessMessage
                        sliderType={ProfileSliderTypes.MY_PROFILE}
                        successMessage={successMessage}
                        setSuccessMessage={setSuccessMessage}>
                    </SliderToastSuccessMessage>}
                {failedMessage.isVisible &&
                    <SliderToastFailedMessage
                        sliderType={ProfileSliderTypes.MY_PROFILE}
                        failedMessage={failedMessage}
                        setFailedMessage={setFailedMessage}>
                    </SliderToastFailedMessage>}
            </div>
        </Fragment>
    );
}
